import React from "react";
import { Table, Tag, Tooltip } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

/**
 * De types die de cloud function naar productEvents schrijft
 * (cloud-functions/src/shared/productEvents.js). Bepaalt zowel de kleur van de tag als het
 * filter op de kolom, zodat er geen types meer buiten het filter kunnen vallen.
 */
const EVENT_TYPES = {
  NEW_PRODUCT: "green",
  DISCONTINUED: "magenta",
  PREORDER_ENABLED: "geekblue",
  OUT_OF_STOCK: "volcano",
  IN_STOCK: "green",
  LOW_IN_STOCK: "orange",
  HIDDEN: "gold",
  VISIBLE: "blue",
};

const EventTypeTag = ({ type, label }) => <Tag color={EVENT_TYPES[type] || "default"}>{label}</Tag>;

const JsonPreview = ({ value }) => {
  if (!value) return null;
  try {
    const str = typeof value === "string" ? value : JSON.stringify(value, null, 2);
    return (
      <Tooltip placement="left" title={<pre style={{ margin: 0, maxWidth: 400 }}>{str}</pre>}>
        <CodeBlock>{typeof value === "string" ? value : JSON.stringify(value)}</CodeBlock>
      </Tooltip>
    );
  } catch (e) {
    return <CodeBlock>{String(value)}</CodeBlock>;
  }
};

const ProductEventsTable = ({ events, loading }) => {
  const { t } = useTranslation();

  const columns = [
    {
      title: t("date"),
      dataIndex: "changedAt",
      key: "changedAt",
      sorter: (a, b) => a.changedAt - b.changedAt,
      render: (ts) => dayjs.unix(ts).format("DD-MM-YYYY HH:mm"),
    },
    {
      title: t("product"),
      dataIndex: "productId",
      key: "productId",
      render: (id, record) => (
        <div>
          <div>{record.productName}</div>
          {!!record?.productCode && <SmallMuted>{record.productCode}</SmallMuted>}
        </div>
      ),
    },
    {
      title: t("type"),
      dataIndex: "type",
      key: "type",
      filters: Object.keys(EVENT_TYPES).map((type) => ({ text: t(`productEvents.${type}`), value: type })),
      onFilter: (value, record) => record.type === value,
      render: (type) => <EventTypeTag type={type} label={t(`productEvents.${type}`)} />,
    },
    {
      title: t("old"),
      dataIndex: "oldValue",
      key: "oldValue",
      render: (v) => <JsonPreview value={v} />,
    },
    {
      title: t("new"),
      dataIndex: "newValue",
      key: "newValue",
      render: (v) => <JsonPreview value={v} />,
    },
    {
      title: t("metadata"),
      dataIndex: "metadata",
      render: (v) => {
        const pairs = Object.entries(v || {})
          .map(([key, value]) => `${key}: ${value}`)
          .join("\n");
        return <JsonPreview value={pairs} />;
      },
      key: "metadata",
    },
  ];

  return (
    <Table
      size="small"
      rowKey={(r, i) => r.id || `${r.productId}-${r.type}-${r.changedAt?.toMillis?.() || i}`}
      columns={columns}
      loading={loading}
      dataSource={events}
      pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"] }}
    />
  );
};

const CodeBlock = styled.code`
  background: #f6f8fa;
  padding: 0 6px;
  border-radius: 4px;
`;

const SmallMuted = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

export default ProductEventsTable;
